import React from "react";

import { getOptions, getBaseUnits, getUnitOptions } from "utils/fields";
import { getDecimalPlaces } from "utils/fieldValidation";
import { useStateSelector } from "utils/field";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import Select from "components/Input/Select";
import MultiSelect from "components/Input/MultiSelect";
import Accordion from "components/Accordion";
import InputWithUnits from "components/Input/InputWithUnits";
import Checkbox from "components/Input/Checkbox";
import NonH2kParam from "assets/images/icons/JSX/NonH2kParam";

import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

export default ({ accessor, change }) => {
    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};

    const currentPrimarySystem = Object.keys(useStateSelector(`${accessor}.system`) || [])[0];

    const { id: capacityId } =
        useStateSelector(`${accessor}.system.${currentPrimarySystem}.specifications.outputCapacity`) || {};
    const isHeatingCapCalculated = capacityId === 1;

    const isFanPowerCalculated = useStateSelector(`${accessor}.fansAndPumps.power.isCalculated`);
    const { id: fanPowerModeId } = useStateSelector(`${accessor}.fansAndPumps.mode`) || {};

    const highPowerUnits = useStateSelector(`${accessor}.fansAndPumps.power.high_u`);
    const lowPowerUnits = useStateSelector(`${accessor}.fansAndPumps.power.low_u`);

    const roomSummary = useStateSelector(`model.modelData.roomSummary`) || {};

    const roomOptions = Object.keys(roomSummary).reduce((acc, roomCat) => {
        return [
            ...acc,
            ...Object.keys(roomSummary[roomCat])
                .map((roomId) => ({
                    label: `${roomSummary[roomCat][roomId]?.name} (${roomCat})` || `Untitled room (${roomCat})`,
                    value: roomId,
                }))
                .filter((el) => !el.value.includes("-default-")),
        ];
    }, []);

    return (
        <>
            <h4 style={{ marginBottom: "1.25rem", marginTop: "1.75rem" }}>Fans &amp; Pumps</h4>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={Select}
                    name={`${accessor}.fansAndPumps.mode`}
                    options={getOptions({ fieldKey: "fanPumpMode" })}
                    label="Mode"
                    placeholder="Choose Mode"
                    fieldKey={"fanPumpMode"}
                    info={"Leave as the default value (Auto)."}
                />
                <Field
                    component={Select}
                    name={`${accessor}.fansAndPumps.power.isCalculated`}
                    options={[
                        {
                            label: "Calculated",
                            value: true,
                        },
                        {
                            label: "User Specified",
                            value: false,
                        },
                    ]}
                    label="Power Type"
                    placeholder="Choose Power Input Type"
                    disabled={isHeatingCapCalculated}
                    info={
                        "Leave as the default value (Calculated). Note that the calculated value will not display in Volta SNAP, but will appear when simulating in HOT2000."
                    }
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.fansAndPumps.power.high`}
                    label="High Speed Power"
                    placeholder="0.00"
                    fieldKey={"fanPumpHighPower"}
                    decimals={getDecimalPlaces("fanPumpHighPower")}
                    change={change}
                    disabled={isFanPowerCalculated || isHeatingCapCalculated}
                    units={{
                        base: getBaseUnits("fanPumpHighPower", modelUnits),
                        options: getUnitOptions("power"),
                        selected: highPowerUnits,
                        unitType: "power",
                        accessor: `${accessor}.fansAndPumps.power.high_u`,
                    }}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.fansAndPumps.power.low`}
                    label="Low Speed Power"
                    placeholder="0.00"
                    fieldKey={"fanPumpLowPower"}
                    decimals={getDecimalPlaces("fanPumpLowPower")}
                    change={change}
                    disabled={isHeatingCapCalculated || isFanPowerCalculated || fanPowerModeId !== 3}
                    units={{
                        base: getBaseUnits("fanPumpLowPower", modelUnits),
                        options: getUnitOptions("power"),
                        selected: lowPowerUnits,
                        unitType: "power",
                        accessor: `${accessor}.fansAndPumps.power.low_u`,
                    }}
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={Checkbox}
                    name={`${accessor}.fansAndPumps.hasEnergyEfficientMotor`}
                    label="Energy Efficient Motor"
                    large
                    className={classes.checkbox}
                    type="checkbox"
                />
            </InputRow>
            <Accordion
                className={sharedClasses.accordion}
                heading={
                    <div className={sharedClasses.accordionIconHeading}>
                        Hydronic Heating System Distribution <NonH2kParam />
                    </div>
                }
                large
            >
                <div>
                    <div className={classes.f280Text}>
                        <h5>
                            Use these fields to define portions of the home served by heat distribution piping passing
                            through unconditioned spaces or adjacent to the perimeter of slab-on-grade foundations for
                            CSA F280-12 calculations.
                        </h5>
                        <p>
                            <strong>Room-by-room:</strong> Select the room(s) served by the distribution system (floor
                            area ignored).
                        </p>
                        <p>
                            <strong>Whole home:</strong> Specify the total floor area (%) served by the distribution
                            system.
                        </p>
                    </div>
                    <InputRow gridTemplate="2fr 1fr 1fr">
                        <Field
                            component={Checkbox}
                            name={`${accessor}.fansAndPumps.f280Hydronic.unconditionedSpace`}
                            label="Enable hydronic losses for F280"
                            setValue={true}
                            large
                            className={classes.checkbox}
                            type="checkbox"
                        />
                    </InputRow>
                    <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                        <Field
                            component={MultiSelect}
                            name={`${accessor}.fansAndPumps.f280Hydronic.selectedRooms`}
                            label="Select rooms served"
                            options={roomOptions}
                            hasSelectAll
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.fansAndPumps.f280Hydronic.percAreaServed`}
                            label="Approximate floor area served"
                            placeholder={0.0}
                            setTouched={true}
                            decimals={getDecimalPlaces("fanPumpLowPower")}
                            change={change}
                            units={{
                                base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                options: ["%"],
                                selected: "%",
                                unitType: "fraction",
                                accessor: `${accessor}.fansAndPumps.f280Hydronic.percAreaServed_u`,
                            }}
                        />
                        <Field
                            component={Select}
                            name={`${accessor}.fansAndPumps.f280Hydronic.circulationType`}
                            options={[
                                {
                                    label: "Gravity",
                                    value: "gravity",
                                },
                                {
                                    label: "Pumped",
                                    value: "pumped",
                                },
                                {
                                    label: "None",
                                    value: "none",
                                },
                            ]}
                            label="Type of Circulation"
                            placeholder="Choose Circulation Type"
                        />
                        <Field
                            component={Checkbox}
                            name={`${accessor}.fansAndPumps.f280Hydronic.insulatedPiping`}
                            label="Insulated piping"
                            setValue={true}
                            large
                            className={classes.checkbox}
                            type="checkbox"
                        />
                    </InputRow>
                </div>
            </Accordion>
        </>
    );
};
